/**
 * Dashboard Home Page
 * Overview page with key metrics and recent activity
 */

import { useState, useEffect } from 'react';
import {
  CalendarIcon,
  UserGroupIcon,
  BriefcaseIcon,
  CurrencyDollarIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { adminAPI } from '../../services/api';

export default function DashboardHome() {
  const [stats, setStats] = useState({
    todayBookings: 0,
    totalClients: 0,
    activeServices: 0,
    monthlyRevenue: 0
  });
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      const { data } = await adminAPI.dashboard.getSummary();
      setStats({
        todayBookings: data.todayBookings || 0,
        totalClients: data.totalClients || 0,
        activeServices: data.activeServices || 0,
        monthlyRevenue: data.monthlyRevenue || 0
      });
      setUpcomingBookings(data.upcomingBookings || []);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Welcome back! Here's what's happening today.</p>
        </div>
        <button
          onClick={fetchDashboardData}
          className="flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          title="Refresh dashboard"
        >
          <ArrowPathIcon className="w-5 h-5 mr-2" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatsCard
          title="Today's Bookings"
          value={stats.todayBookings}
          icon={CalendarIcon}
          color="blue"
        />
        <StatsCard
          title="Total Clients"
          value={stats.totalClients}
          icon={UserGroupIcon}
          color="green"
        />
        <StatsCard
          title="Active Services"
          value={stats.activeServices}
          icon={BriefcaseIcon}
          color="purple"
        />
        <StatsCard
          title="Monthly Revenue"
          value={`$${stats.monthlyRevenue}`}
          icon={CurrencyDollarIcon}
          color="yellow"
        />
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickActionButton
            href="/admin/bookings"
            label="View Bookings"
            icon={CalendarIcon}
          />
          <QuickActionButton
            href="/admin/staff"
            label="Manage Staff"
            icon={UserGroupIcon}
          />
          <QuickActionButton
            href="/admin/services"
            label="Manage Services"
            icon={BriefcaseIcon}
          />
          <QuickActionButton
            href="/admin/clients"
            label="View Clients"
            icon={UserGroupIcon}
          />
        </div>
      </div>

      {/* Upcoming Bookings */}
      <div className="mt-6 bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Bookings</h2>
        {upcomingBookings.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No upcoming bookings
          </p>
        ) : (
          <div className="space-y-3">
            {upcomingBookings.map((booking) => (
              <UpcomingBookingCard key={booking.id} booking={booking} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatsCard({ title, value, icon: Icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    yellow: 'bg-yellow-500'
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
        </div>
        <div className={`${colorClasses[color]} rounded-lg p-3`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
}

function QuickActionButton({ href, label, icon: Icon }) {
  return (
    <a
      href={href}
      className="flex items-center justify-center space-x-2 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 hover:text-gray-900"
    >
      <Icon className="w-5 h-5" />
      <span className="font-medium">{label}</span>
    </a>
  );
}

function UpcomingBookingCard({ booking }) {
  const startTime = new Date(booking.start_time);
  const endTime = new Date(booking.end_time);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  };

  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
      <div className="flex-1">
        <div className="flex items-center space-x-3">
          <div>
            <p className="font-medium text-gray-900">
              {booking.client_first_name} {booking.client_last_name}
            </p>
            <p className="text-sm text-gray-600">{booking.service_name}</p>
            {booking.staff_first_name && (
              <p className="text-xs text-gray-500">
                with {booking.staff_first_name} {booking.staff_last_name}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-4">
        <div className="text-right">
          <p className="text-sm font-medium text-gray-900">
            {formatDate(startTime)}
          </p>
          <p className="text-sm text-gray-600">
            {formatTime(startTime)} - {formatTime(endTime)}
          </p>
        </div>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          statusColors[booking.status] || 'bg-gray-100 text-gray-800'
        }`}>
          {booking.status}
        </span>
      </div>
    </div>
  );
}
